<template>
  <div class="dx-game-group">
    <div class="input-group" v-if="owner">
      <input v-model="search" class="form-control" :placeholder="$t('userAdd')" @keydown.enter="onAddUser" ref="search" />
      <div class="input-group-append">
        <dx-button primary :loading="addingUser" :disabled="addingUser" @click.native="onAddUser">
          <icon name="plus" />
        </dx-button>
      </div>
    </div>
    <div class="row">
      <div class="col-6 col-lg-4"
        v-for="player in group.players"
        :key="player.user.id"
        >
        <dx-user-avatar
          :id="player.user.id"
          :name="player.user.nickname"
          :image="player.user.image"
          :items="player.user.items"
          />
        <div class="dx-game-group__user-actions">
          <span :class="stateClasses(player)">{{ playerState(player) }}</span>
          <dx-button
            success
            small
            :loading="isAcceptingUser(player.id)"
            :disabled="acceptingUser !== false"
            @click.native="accept(player)"
            v-if="isCurrentPlayerAndPending(player)"
            >
            <icon name="check" />
          </dx-button>
          <dx-button-confirm
            v-if="showRemoveButtonUser(player)"
            danger
            small
            :disabled="removingUser !== false"
            :loading="isRemovingUser(player.id)"
            @click="onDeleteGameGroupUser(player)"
            >
            <icon name="times" />
          </dx-button-confirm>
        </div>
      </div>
    </div>
    <div class="dx-game-group__actions" v-if="group.id">
      <div class="row align-items-end">
        <div class="col-sm-7">
          <div class="form-group input-group-sm">
            <label>{{ $t('gameGroupName') }}</label>
            <input v-model="name" class="form-control" @keyup="updateGroupName" :disabled="!owner" />
          </div>
        </div>
        <div class="col-sm-5">
          <dx-button-confirm :disabled="removingGroup" :loading="removingGroup" warning small @click="onDeleteGameGroup" v-if="owner">
            {{ $t('removeGroup') }}
          </dx-button-confirm>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-awesome/icons/times'
import 'vue-awesome/icons/plus'
import 'vue-awesome/icons/check'
import { mapActions, mapGetters } from 'vuex'
import DxButton from './DxButton.vue'
import DxUserAvatar from './DxUserAvatar.vue'
import DxButtonConfirm from './DxButtonConfirm.vue'

export default {
  name: 'dx-game-group',
  components: {
    DxButton,
    DxUserAvatar,
    DxButtonConfirm
  },
  props: {
    owner: Boolean,
    group: Object
  },
  data () {
    return {
      removingUser: false,
      acceptingUser: false,
      removingGroup: false,
      addingUser: false,
      search: '',
      name: ''
    }
  },
  methods: {
    ...mapActions([
      'createGameGroup',
      'updateGameGroup',
      'deleteGameGroup',
      'deleteGameGroupUser',
      'updateGameGroupUser'
    ]),
    onDeleteGameGroupUser (player) {
      this.removingUser = player.id
      if (player.user.id === this.user.id) {
        let data = {
          id: player.id,
          state: player.state === 'pending' ? 'refused' : 'left'
        }
        this.updateGameGroupUser(data).then((result) => {
          this.removingUser = false
          if (result) {
            this.$emit(`${data.state}-game-group-user`, player.id)
          }
        }).catch(() => {
          this.removingUser = false
        })
      } else {
        this.deleteGameGroupUser(player.id).then((result) => {
          this.removingUser = false
          if (result) {
            this.$emit('delete-game-group-user', player.id)
          }
        }).catch(() => {
          this.removingUser = false
        })
      }
    },
    onDeleteGameGroup () {
      this.removingGroup = true
      this.deleteGameGroup(this.group.id).then((result) => {
        this.removingGroup = false
        if (result) {
          this.$emit('delete-game-group', this.group.id)
        }
      }).catch(() => {
        this.removingGroup = false
      })
    },
    isRemovingUser (id) {
      return this.removingUser === id
    },
    isAcceptingUser (id) {
      return this.acceptingUser === id
    },
    onAddUser () {
      this.$refs.search.select()
      if (this.search) {
        this.addingUser = true
        let data = {
          id: this.group.id,
          players: [this.search]
        }
        data.name = this.name || 'my group'
        if (this.group.id) {
          this.updateGameGroup(data).then((result) => {
            this.addingUser = false
            if (result) {
              this.$emit('updated-game-group', result)
            }
          }).catch(() => {
            this.addingUser = false
          })
        } else {
          this.createGameGroup(data).then((result) => {
            this.addingUser = false
            if (result) {
              this.$emit('created-game-group', result)
            }
          }).catch(() => {
            this.addingUser = false
          })
        }
      }
    },
    updateGroupName () {
      clearTimeout(this.throttledUpdate)
      this.throttledUpdate = setTimeout(() => {
        this.updateGameGroup({ id: this.group.id, name: this.name })
      }, 500)
    },
    stateClasses (player) {
      return {
        'badge': true,
        'badge-secondary': player.state === 'pending',
        'badge-success': player.state === 'accepted' || player.state === 'owner',
        'badge-warning': player.state === 'refused',
        'badge-danger': player.state === 'left'
      }
    },
    playerState (player) {
      return this.$t(player.state)
    },
    showRemoveButtonUser (player) {
      return (
        (this.owner && this.user.id !== player.user.id) ||
        (!this.owner && this.user.id === player.user.id && this.validStateToRemove(player.state))
      )
    },
    isCurrentPlayerAndPending (player) {
      return !this.owner && this.user.id === player.user.id && player.state === 'pending'
    },
    accept (player) {
      this.acceptingUser = player.id
      let data = {
        id: player.id,
        state: 'accepted'
      }
      this.updateGameGroupUser(data).then((result) => {
        this.acceptingUser = false
      }).catch(() => {
        this.acceptingUser = false
      })
    },
    validStateToRemove (state) {
      return ['pending', 'accepted'].indexOf(state) > -1
    }
  },
  created () {
    this.name = this.group.name
  },
  computed: {
    ...mapGetters({
      user: 'getUser'
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/components/DxGameGroup.vue